import React, { useState, useEffect } from 'react';
import api from '../api/axios';

type PermissionLevel = 'none' | 'view' | 'full';

interface ModuleInfo { key: string; label: string; icon: string; }

const modules: ModuleInfo[] = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'products', label: 'Mahsulotlar', icon: '🧪' },
  { key: 'suppliers', label: 'Yetkazib beruvchilar', icon: '🏭' },
  { key: 'clients', label: 'Mijozlar', icon: '👔' },
  { key: 'sales_quotations', label: 'Sotuv takliflari', icon: '📈' },
  { key: 'service_quotations', label: 'Xizmat takliflari', icon: '🛠️' },
  { key: 'tenders', label: 'Tenderlar', icon: '🏛️' },
  { key: 'purchases', label: "Xarid so'rovlari", icon: '📋' },
  { key: 'sales_cases', label: 'Sales Cases', icon: '💼' },
  { key: 'service_cases', label: 'Service Cases', icon: '🔧' },
  { key: 'accounting', label: 'Buxgalteriya', icon: '📒' },
  { key: 'warehouse', label: 'Ombor boshqaruvi', icon: '🏪' },
  { key: 'service_module', label: 'Servis & Instrumentlar', icon: '🔬' },
  { key: 'service_acts', label: 'Dalolatnomalar', icon: '📑' },
  { key: 'users', label: 'Foydalanuvchilar', icon: '👥' },
];

const roleGroups = [
  { label: 'Rahbariyat', roles: ['director'] },
  { label: 'Savdo', roles: ['sales_head', 'sales_manager'] },
  { label: 'Servis', roles: ['service_head', 'service_engineer'] },
  { label: 'Ombor & Logistika', roles: ['warehouse_head', 'logistic_head', 'logist'] },
  { label: 'Moliya', roles: ['chief_accountant', 'accountant'] },
  { label: 'Boshqa', roles: ['hr_manager', 'lawyer'] },
];

const roleLabels: Record<string, string> = {
  director: 'Direktor',
  sales_head: "Savdo bo'limi rahbari",
  sales_manager: 'Savdo menejeri',
  service_head: "Servis bo'limi rahbari",
  service_engineer: 'Servis injener',
  warehouse_head: 'Ombor rahbari',
  logistic_head: "Logistika bo'limi boshlig'i",
  logist: 'Logist',
  chief_accountant: 'Bosh buxgalter',
  accountant: 'Buxgalter',
  hr_manager: 'HR menejeri',
  lawyer: 'Yurist',
};

const levelConfig: Record<PermissionLevel, { label: string; color: string; icon: string }> = {
  none: { label: "Kirish yo'q", color: 'bg-red-100 text-red-700 border-red-300', icon: '🚫' },
  view: { label: "Ko'rish", color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '👁️' },
  full: { label: "To'liq", color: 'bg-green-100 text-green-700 border-green-300', icon: '✅' },
};

const Permissions: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState('sales_manager');
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRoleList, setShowRoleList] = useState(false);

  const fetchPermissions = async (role: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/permissions/${role}`);
      const permsMap: Record<string, PermissionLevel> = {};
      res.data.permissions.forEach((p: any) => { permsMap[p.module] = p.level; });
      setPermissions(permsMap);
      setHasChanges(false);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPermissions(selectedRole); }, [selectedRole]);

  const handleLevelChange = (module: string, level: PermissionLevel) => {
    setPermissions({ ...permissions, [module]: level });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        permissions: Object.entries(permissions).map(([module, level]) => ({ module, level }))
      };
      await api.put(`/permissions/${selectedRole}`, payload);
      setHasChanges(false);
      alert('Ruxsatlar saqlandi');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Xatolik');
    } finally { setSaving(false); }
  };

  const applyPreset = (preset: 'all_view' | 'all_full' | 'all_none') => {
    const newPerms: Record<string, PermissionLevel> = {};
    modules.forEach(m => {
      newPerms[m.key] = preset === 'all_view' ? 'view' : preset === 'all_full' ? 'full' : 'none';
    });
    setPermissions(newPerms);
    setHasChanges(true);
  };

  return (
    <div className="p-3 md:p-6">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Ruxsatlar boshqaruvi</h2>
        <p className="text-gray-500 text-xs md:text-sm mt-1">Har bir lavozim uchun modullarga kirish darajasini belgilang</p>
      </div>

      {/* Mobile: rol tanlash dropdown */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setShowRoleList(!showRoleList)}
          className="w-full flex justify-between items-center bg-white rounded-xl shadow px-4 py-3 text-sm font-medium"
        >
          <span>{roleLabels[selectedRole]}</span>
          <span className={`transition-transform ${showRoleList ? 'rotate-180' : ''}`}>▾</span>
        </button>
        {showRoleList && (
          <div className="bg-white rounded-xl shadow mt-2 overflow-hidden">
            {roleGroups.map(group => (
              <div key={group.label}>
                <p className="text-xs text-gray-400 font-medium uppercase px-4 py-2 bg-gray-50">{group.label}</p>
                {group.roles.map(role => (
                  <button key={role} onClick={() => { setSelectedRole(role); setShowRoleList(false); }}
                    className={`w-full text-left px-4 py-3 text-sm border-b border-gray-50 ${selectedRole === role ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'}`}>
                    {roleLabels[role]}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
        {/* Desktop: rol tanlash sidebar */}
        <div className="hidden md:block col-span-1">
          <div className="bg-white rounded-xl shadow p-4 sticky top-4">
            <h3 className="font-semibold text-gray-700 mb-3 text-sm">Lavozimni tanlang</h3>
            {roleGroups.map(group => (
              <div key={group.label} className="mb-3">
                <p className="text-xs text-gray-400 font-medium uppercase mb-1">{group.label}</p>
                {group.roles.map(role => (
                  <button key={role} onClick={() => setSelectedRole(role)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition ${selectedRole === role ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                    {roleLabels[role]}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Ruxsatlar */}
        <div className="md:col-span-3">
          <div className="bg-white rounded-xl shadow p-4 md:p-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <h3 className="text-base md:text-lg font-bold text-gray-800">
                {roleLabels[selectedRole]} uchun ruxsatlar
              </h3>
              <div className="flex gap-1 md:gap-2 flex-wrap">
                <button onClick={() => applyPreset('all_view')}
                  className="px-2 md:px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs hover:bg-yellow-100">
                  Hammasi ko'rish
                </button>
                <button onClick={() => applyPreset('all_full')}
                  className="px-2 md:px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs hover:bg-green-100">
                  Hammasi to'liq
                </button>
                <button onClick={() => applyPreset('all_none')}
                  className="px-2 md:px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs hover:bg-red-100">
                  Hammasi yopiq
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-10 text-gray-400">Yuklanmoqda...</div>
            ) : (
              <div className="space-y-2">
                {modules.map(mod => {
                  const level = permissions[mod.key] || 'view';
                  return (
                    <div key={mod.key} className="flex flex-col md:flex-row md:items-center md:justify-between p-3 border rounded-lg hover:bg-gray-50 gap-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{mod.icon}</span>
                        <span className="font-medium text-gray-700 text-sm">{mod.label}</span>
                      </div>
                      <div className="flex gap-1 md:gap-2">
                        {(['none', 'view', 'full'] as PermissionLevel[]).map(lvl => (
                          <button key={lvl}
                            onClick={() => handleLevelChange(mod.key, lvl)}
                            className={`flex-1 md:flex-none px-2 md:px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition ${level === lvl ? levelConfig[lvl].color : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                            {levelConfig[lvl].icon} <span className="hidden md:inline">{levelConfig[lvl].label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {hasChanges && (
              <div className="mt-4 md:mt-6 flex justify-end gap-3 sticky bottom-0 bg-white pt-4 border-t">
                <button onClick={() => fetchPermissions(selectedRole)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">
                  Bekor qilish
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                  {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permissions;
